/**
 * Loading-timeout safety state machine for the player-kanban board.
 * @remarks
 * Extracted from MainContainer (SE-5237). Owns the 45s "is the board stuck
 * loading?" guard and the retry that recovers from it.
 *
 * Two defects this hook fixes versus the previous inline implementation:
 *
 * 1. **Retry never recovered.** The old retry only reset the timeout UI and
 *    bumped a trigger no query consumed, so a stuck/never-resolving load
 *    (a backend query that hangs without erroring, or a transient
 *    complexity-limit 400) re-entered the same 45s timeout indefinitely. The
 *    caller now passes real refetch callbacks that this hook invokes on retry.
 *
 * 2. **The arming condition disagreed with the loading gate.** The timeout was
 *    only armed on `boardsLoading || isLoading`, but the UI also shows a spinner
 *    when `activeBoard` is null while boards exist (`!activeBoard &&
 *    !noBoardsExist`). In that state the board spun forever with no timeout ever
 *    firing. The hook now arms on the same `isShowingLoading` signal the gate
 *    uses, so every spinner state is bounded.
 *
 * The timed-out flags are *derived* from a monotonic retry epoch rather than
 * reset inside the effect, so there is no synchronous set-state-in-effect: the
 * effect only sets state from the timer callback, and clearing happens when
 * `isShowingLoading` goes false (the timer is torn down by cleanup).
 */
import { useCallback, useEffect, useState } from "react";

import * as Sentry from "@sentry/react-native";

/** Loading-timeout window in milliseconds. */
export const KANBAN_LOADING_TIMEOUT_MS = 45000;

/**
 * Inputs for the kanban loading-timeout state machine.
 */
interface UseKanbanLoadingTimeoutParams {
  /** Whether the board manager (boards list) is still loading. */
  boardsLoading: boolean;
  /** Whether the active board's data (phases/cards) is still loading. */
  isLoading: boolean;
  /**
   * Whether the UI is currently showing a loading state — the same gate the
   * component renders on, so the timeout covers every spinner state including
   * `activeBoard === null` while boards exist.
   */
  isShowingLoading: boolean;
  /** Active board id, for Sentry context. */
  activeBoardId?: string;
  /** Active board name, for Sentry context. */
  activeBoardName?: string;
  /** Player source (watchlist/roster), for Sentry context. */
  sourceType: string;
  /** Re-issue the board-manager queries on retry. */
  refreshBoards?: () => unknown;
  /** Re-issue the active board's queries (phases/cards) on retry. */
  refetchBoard?: () => unknown;
}

/**
 * Result of the kanban loading-timeout state machine.
 */
interface UseKanbanLoadingTimeoutResult {
  /** True once the board has been stuck loading past the timeout window. */
  showTimeoutError: boolean;
  /** True once the timeout has fired (drives the "taking longer" hint). */
  hasTimedOut: boolean;
  /** Retry handler: clears the timeout state and re-fetches board data. */
  handleRetryLoading: () => void;
}

/**
 * Bounds kanban loading with a 45s timeout and a retry that actually refetches.
 * @param params - Loading signals, Sentry context, and refetch callbacks.
 * @param params.boardsLoading - Whether the boards list is still loading.
 * @param params.isLoading - Whether the active board's data is still loading.
 * @param params.isShowingLoading - Whether the UI is showing a loading state.
 * @param params.activeBoardId - Active board id, for Sentry context.
 * @param params.activeBoardName - Active board name, for Sentry context.
 * @param params.sourceType - Player source, for Sentry context.
 * @param params.refreshBoards - Re-issue the board-manager queries on retry.
 * @param params.refetchBoard - Re-issue the active board's queries on retry.
 * @returns Timeout flags and a retry handler for the loading UI.
 */
export const useKanbanLoadingTimeout = ({
  boardsLoading,
  isLoading,
  isShowingLoading,
  activeBoardId,
  activeBoardName,
  sourceType,
  refreshBoards,
  refetchBoard,
}: UseKanbanLoadingTimeoutParams): UseKanbanLoadingTimeoutResult => {
  // `timedOutEpoch` records the retry epoch at which the timer last fired.
  // `retryEpoch` bumps on each retry. The board is considered timed-out only
  // while it is *still* loading AND the latest fire matches the current epoch,
  // so a retry (which bumps the epoch) clears the flags without resetting state
  // inside an effect.
  const [retryEpoch, setRetryEpoch] = useState(0);
  const [timedOutEpoch, setTimedOutEpoch] = useState<number | null>(null);

  const hasTimedOut = isShowingLoading && timedOutEpoch === retryEpoch;
  const showTimeoutError = hasTimedOut;

  const handleRetryLoading = useCallback(() => {
    setRetryEpoch(prev => prev + 1);

    // Re-issue the underlying queries so a stuck/never-resolving load can
    // recover. Errors are swallowed — each query reports via its own onError,
    // and the loading/timeout machinery handles the rest.
    void Promise.resolve(refreshBoards?.()).catch(() => {});
    void Promise.resolve(refetchBoard?.()).catch(() => {});

    Sentry.captureMessage("User retrying after Kanban timeout", "info");
  }, [refreshBoards, refetchBoard]);

  useEffect(() => {
    if (!isShowingLoading) {
      return;
    }

    const timeoutId = setTimeout(() => {
      setTimedOutEpoch(retryEpoch);

      Sentry.captureException(
        new Error("Kanban loading timeout after 45 seconds"),
        {
          tags: {
            component: "MainContainer",
            feature: "player-kanban",
            timeout_duration: KANBAN_LOADING_TIMEOUT_MS,
          },
          extra: {
            boardsLoading,
            isLoading,
            activeBoard: activeBoardId,
            boardName: activeBoardName,
            sourceType,
          },
        }
      );
    }, KANBAN_LOADING_TIMEOUT_MS);

    return () => clearTimeout(timeoutId);
  }, [
    isShowingLoading,
    retryEpoch,
    boardsLoading,
    isLoading,
    activeBoardId,
    activeBoardName,
    sourceType,
  ]);

  return { showTimeoutError, hasTimedOut, handleRetryLoading };
};
